import { useTranslation } from "react-i18next";

import { useAcceptDeclineWorkspaceInvitation, useModal } from "@hooks";
import { getBuildingById, useAppDispatch, useAppSelector, withAsyncThunkErrorHandling } from "@lib/store";
import { actions } from "@lib/store/src/data/timeslots/actions";
import { getConfirmedWorkspaceReservationsForWorkday } from "@lib/store/src/data/timeslots/selectors";
import { OfficeWorkday, WorkspaceReservation } from "@lib/store/src/data/timeslots/apiTypes";
import { ModalPageBottom } from "src/components/organisms/ModalPageBottom";
import { useEditWorkspaceReservation } from "../../calendar/CalendarWeekDay/components/QuickOfficeDayButton";
import { isBeforeToday } from "@lib/logic";

type Props = {
  workday: OfficeWorkday | null;
  workspaceReservation: WorkspaceReservation;
  isSaving: boolean;
  setIsSaving: React.Dispatch<React.SetStateAction<boolean>>;
};

export const Footer = ({ workday, workspaceReservation, setIsSaving, isSaving }: Props) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { closeModal } = useModal();

  const currentWorkdayConfirmedWorkspaceReservations = useAppSelector(
    getConfirmedWorkspaceReservationsForWorkday(workday?.id ?? null),
  );

  const { allowWorkdayWithoutWorkspace } =
    useAppSelector(state => getBuildingById(state, workday?.buildingId || "")) ?? {};

  const editFlow = useEditWorkspaceReservation(workspaceReservation);
  const isPending = workspaceReservation.status === "InvitationPending";
  const isConfirmed = workspaceReservation.status === "Confirmed";
  const isInvitation = workspaceReservation.invitationTo !== null;

  const {
    isSaving: isSavingInvitation,
    onAcceptInvitation,
    onDeclineInvitation,
  } = useAcceptDeclineWorkspaceInvitation(workspaceReservation);

  const rightButton: { label: string; onClick: () => void; destructive: boolean } = isPending
    ? {
        label: t("translation:Accept"),
        destructive: false,
        onClick: () => onAcceptInvitation(closeModal),
      }
    : isConfirmed
      ? {
          label: t("workplace:DeleteBooking"),
          destructive: true,
          onClick: async () => {
            setIsSaving(true);

            let result;

            if (allowWorkdayWithoutWorkspace || currentWorkdayConfirmedWorkspaceReservations.length > 1 || !workday) {
              result = await dispatch(
                withAsyncThunkErrorHandling(() => actions.cancelWorkspaceReservation({ workspaceReservation })),
              );
            } else {
              result = await dispatch(
                withAsyncThunkErrorHandling(() => actions.cancelWorkdayIncludingReservations({ workday })),
              );
            }

            if (!result.success) {
              alert(result.result.message);
            } else {
              closeModal();
            }

            setIsSaving(false);
          },
        }
      : {
          label: "TODO: label",
          destructive: false,
          onClick: () => alert("TODO: handle this case"),
        };

  const leftButton: { label: string; onClick?: () => void; destructive: boolean } =
    workspaceReservation.status === "InvitationPending"
      ? {
          label: t("translation:Decline"),
          destructive: true,
          onClick: () => onDeclineInvitation(closeModal),
        }
      : !isInvitation
        ? {
            label: t("Edit"),
            destructive: false,
            onClick: () => {
              if (!workday) throw new Error("Attempt to edit a reservation that has no workday");
              editFlow.openModal(workday);
            },
          }
        : {
            label: "",
            destructive: true,
            onClick: undefined,
          };

  if (isBeforeToday(workspaceReservation.localStart)) {
    return null;
  }

  return (
    <ModalPageBottom
      // Right button
      buttonLabel={rightButton.label}
      buttonType={rightButton.destructive ? "primary destructive" : "primary"}
      buttonDisabled={isSaving || isSavingInvitation}
      destructive={rightButton.destructive}
      onClick={rightButton.onClick}
      // Left button
      cancelButtonLabel={leftButton.label}
      cancelButtonType={leftButton.destructive ? "secondary" : "tertiary"}
      cancelButtonDisabled={isSaving || isSavingInvitation}
      onCancel={leftButton.onClick}
    />
  );
};
